import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Briefcase, Globe, CheckCircle } from 'react-feather';
import logo from '../../../assets/logo.png';

// --- STYLED COMPONENTS ---
const LoginPageContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100vh;
    background-color: transparent;
    overflow: hidden;

    @media (min-width: 1024px) {
        flex-direction: row;
    }
`;

// ============ LEFT PANEL ============
const DesktopPanel = styled.div`
  display: none;
  @media (min-width: 1024px) {
    display: flex;
    flex-direction: column;
    width: 50%;
    flex: 0 0 50%;
    background: linear-gradient(135deg, ${p => p.theme.colors.text.dark} 0%, #111827 100%);
    justify-content: center;
    padding: 60px 80px;
    position: relative;
  }
`;

const PanelHeading = styled.h1`
  font-family: 'Inter', sans-serif;
  color: ${p => p.theme.colors.background.paper};
  font-size: 28px;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.5px;
  margin: 0 0 20px 0;
`;

const PanelDescription = styled.p`
  font-family: 'Inter', sans-serif;
  color: ${p => p.theme.colors.text.disabled};
  font-size: 16px;
  font-weight: 300;
  line-height: 1.7;
  margin: 0 0 60px 0;
  max-width: 420px;
`;

const DesktopLogoImage = styled.img`
  width: 380px;
  height: auto;
  object-fit: contain;
  filter: brightness(0) invert(1);
  margin-top: 40px;
  margin-bottom: 80px;
`;

const TrustBadgesRow = styled.div`
  display: flex;
  gap: 48px;
  margin-top: auto;
`;

const TrustBadge = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TrustBadgeIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background-color: rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
`;

const TrustBadgeTitle = styled.span`
  font-family: 'Inter', sans-serif;
  color: ${p => p.theme.colors.background.paper};
  font-size: 14px;
  font-weight: 600;
`;

const TrustBadgeDesc = styled.span`
  font-family: 'Inter', sans-serif;
  color: ${p => p.theme.colors.text.secondary};
  font-size: 13px;
  font-weight: 400;
  line-height: 1.4;
  max-width: 180px;
`;

// ============ RIGHT PANEL ============
const RightPanel = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: ${p => p.theme.colors.background.paper};
  min-height: 100dvh;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;

  @media (min-width: 1024px) {
    width: 50%;
    flex: 0 0 50%;
    justify-content: center;
    align-items: center;
    padding: 40px 40px 50px 40px;
  }
`;

const FormCard = styled.div`
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  
  @media (max-width: 1023px) {
    max-width: 100%;
    flex: 1;
  }
`;

const MobileHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 30px 20px 30px;
  text-align: center;
  position: relative;

  @media (min-width: 1024px) {
    display: none;
  }
`;

const MobileLogo = styled.img`
  width: 180px; 
  height: 90px;
  object-fit: contain;
  margin-bottom: 10px;
  filter: ${p => p.theme.isDark ? 'brightness(0) invert(1)' : 'brightness(0)'};
`;



// --- Title / Subtitle ---
const FormTitle = styled.h2`
  font-family: 'Inter', sans-serif;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.5px;
  color: ${p => p.theme.colors.text.dark};
  margin: 0 0 8px 0;
  text-align: center;
  padding: 0 30px;

  @media (min-width: 1024px) {
    padding: 0;
    text-align: left;
  }
`;

const FormSubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: ${p => p.theme.colors.text.secondary};
  margin: 0 0 40px 0;
  text-align: center;
  padding: 0 30px;

  @media (min-width: 1024px) {
    padding: 0;
    text-align: left;
  }
`;

// --- Options Section ---
const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 30px;

  @media (min-width: 1024px) {
    padding: 0;
  }
`;

const ChoiceCard = styled.button`
  background-color: ${p => p.theme.colors.background.paper};
  width: 100%;
  padding: 24px;
  border-radius: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  text-align: left;
  gap: 20px;
  border: 1px solid ${p => p.theme.colors.border.main};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    border-color: ${p => p.theme.colors.border.hover};
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const IconWrapper = styled.div`
  background-color: ${p => p.theme.colors.background.selected};
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.theme.colors.text.dark};
  flex-shrink: 0;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${p => p.theme.colors.text.dark};
  margin-bottom: 4px;
`;

const CardDescription = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: ${p => p.theme.colors.text.secondary};
`;

// --- Footer ---
const FooterRow = styled.div`
  font-family: 'Inter', sans-serif;
  display: flex;
  justify-content: center;
  margin-top: 40px;
  padding: 0 30px;
  font-size: 14px;
  color: ${p => p.theme.colors.text.secondary};

  @media (min-width: 1024px) {
    padding: 0;
    justify-content: flex-start;
  }
  
  button {
    background: none;
    border: none;
    color: ${p => p.theme.colors.text.dark};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    margin-left: 6px;
    padding: 0;
    font-family: inherit;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const LegalFooter = styled.div`
  font-family: 'Inter', sans-serif;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  padding: 20px;
  font-size: 11px;
  color: ${p => p.theme.colors.text.disabled};
  flex-wrap: wrap;
  text-align: center;
  line-height: 1.5;

  @media (min-width: 1024px) {
    position: absolute;
    bottom: 12px;
    left: 0;
    right: 0;
  }

  span {
    cursor: default;
  }

  span:not(:first-child) {
    cursor: pointer;
    &:hover {
      color: ${p => p.theme.colors.primary.light};
    }
  }
`;

// --- COMPONENT ---
const CreateUserScreen = () => {
  const navigate = useNavigate();

  return (
    <LoginPageContainer>
      {/* ===== LEFT PANEL (desktop only) ===== */}
      <DesktopPanel>
        <DesktopLogoImage src={logo} alt="NYBANQ" />
        <PanelHeading>
          Banking{'\n'}Without{'\n'}Borders
        </PanelHeading>
        <PanelDescription>
          Multi-currency accounts, global transfers, and crypto — all in one secure platform.
        </PanelDescription>

        <TrustBadgesRow>
          <TrustBadge>
            <TrustBadgeIcon>
              <CheckCircle size={20} color="currentColor" />
            </TrustBadgeIcon>
            <TrustBadgeTitle>Bank-Grade Security</TrustBadgeTitle>
            <TrustBadgeDesc>Enterprise-level compliance.</TrustBadgeDesc>
          </TrustBadge>
          <TrustBadge>
            <TrustBadgeIcon>
              <Globe size={20} color="currentColor" />
            </TrustBadgeIcon>
            <TrustBadgeTitle>Multi-Currency</TrustBadgeTitle>
            <TrustBadgeDesc>USD, EUR.</TrustBadgeDesc>
          </TrustBadge>
        </TrustBadgesRow>
      </DesktopPanel>

      {/* ===== RIGHT PANEL ===== */}
      <RightPanel>
        <FormCard>
          {/* Mobile-only logo */}
          <MobileHeader>
            <MobileLogo src={logo} alt="NYBANQ Logo" />
          </MobileHeader>

          {/* Form Header */}
          <FormTitle>Create an account</FormTitle>
          <FormSubtitle>Choose the type of account that matches your needs.</FormSubtitle>

          {/* Options */}
          <OptionsContainer>
            <ChoiceCard
              onClick={() => navigate('/register', { state: { accountType: 'business' } })}
              data-testid="create-account-business"
            >
              <IconWrapper>
                <Briefcase size={24} color="currentColor" />
              </IconWrapper>
              <CardContent>
                <CardTitle>Business Account</CardTitle>
                <CardDescription>Business Checking & Savings Accounts </CardDescription>
              </CardContent>
            </ChoiceCard>

            <ChoiceCard
              onClick={() => navigate('/register', { state: { accountType: 'cross_border' } })}
              data-testid="create-account-crossborder"
            >
              <IconWrapper>
                <Globe size={24} color="currentColor" />
              </IconWrapper>
              <CardContent>
                <CardTitle>Cross Border Payments</CardTitle>
                <CardDescription>Global transfers and multi-currency solutions</CardDescription>
              </CardContent>
            </ChoiceCard>
          </OptionsContainer>

          {/* Footer links */}
          <FooterRow>
            Already have an account? <button onClick={() => navigate('/login')} data-testid="login-link">Log in</button>
          </FooterRow>

        </FormCard>

        <LegalFooter>
          <span>© 2026 NYBANQ. All rights reserved.</span>
          <span>Privacy Policy</span>
          <span>Terms & Conditions</span>
          <span onClick={() => navigate('/consents')}>NYBANQ Customer Consents, Disclosures, and Acknowledgments</span>
        </LegalFooter>
      </RightPanel>
    </LoginPageContainer>
  );
};

export default CreateUserScreen;